import { validDocumentTypeForInsurance } from './documentMaps';

/**
 * Auto-disables insurance if any passenger or purchaser has an ineligible document type.
 *
 * @param {object} params - Configuration object.
 * @param {object} params.purchase - Purchase object containing wantsInsurance, updatingInsurance, and token.
 * @param {object} params.features - Features configuration object.
 * @param {Function} params.onToggleWantsInsurance - Function to toggle insurance state.
 * @param {Function} params.getInsuranceEligibility - Function that returns eligibility data with hasIneligibleDoc property.
 * @param {object} [params.additionalConditions] - Additional feature flag conditions (optional).
 * @param {Function} [params.onCloseInsuranceModal] - Optional callback to close insurance modal when ineligible.
 * @returns {boolean} - Returns true if insurance was auto-disabled, false otherwise.
 */
export function autoDisableInsuranceIfIneligible({
  purchase,
  features,
  onToggleWantsInsurance,
  getInsuranceEligibility,
  additionalConditions = {},
  onCloseInsuranceModal,
}) {
  const { wantsInsurance, updatingInsurance, token } = purchase;

  // Build the condition for when to auto-disable
  let shouldAutoDisableInsurance = !features.PASSENGERS_INSURANCE_ENABLED;

  // Apply additional conditions if provided
  if (Object.keys(additionalConditions).length > 0) {
    shouldAutoDisableInsurance =
      shouldAutoDisableInsurance &&
      Object.values(additionalConditions).every((condition) => !condition);
  }

  // Check if we should proceed with auto-disable logic
  if (!shouldAutoDisableInsurance || updatingInsurance) {
    return false;
  }

  // Check eligibility
  const { hasIneligibleDoc } = getInsuranceEligibility();

  if (hasIneligibleDoc) {
    // Auto-disable insurance if it's currently enabled
    if (wantsInsurance) {
      onToggleWantsInsurance(token, false);
    }

    // Close modal if callback provided
    if (onCloseInsuranceModal) {
      onCloseInsuranceModal();
    }

    return true;
  }

  return false;
}

/**
 * Gets insurance eligibility based on passenger and purchaser document types.
 * This is a non-memoized version for non-React contexts.
 * For React components, prefer using the useInsuranceEligibility hook.
 *
 * @param {object} params - Configuration object.
 * @param {object} [params.passengersForm] - Redux form object for passengers.
 * @param {object} [params.purchaserForm] - Redux form object for purchaser.
 * @param {object} [params.purchaser] - Purchaser object from props.
 * @param {object} [params.purchase] - Purchase object containing current passengers.
 * @returns {object} - Returns object with hasIneligibleDoc (boolean) and passengerDocs (array).
 */
export function getInsuranceEligibilityFromForms({
  passengersForm,
  purchaserForm,
  purchaser,
  purchase,
}) {
  const { passengers: currentPassengers } = purchase || {};

  // Get passenger documents from form or purchase
  const passengerSources = passengersForm?.values?.passengers || currentPassengers || [];
  const passengerDocs = passengerSources.map((p) => p.documentType).filter(Boolean);

  // Get purchaser document from form or purchaser object
  const purchaserDoc = purchaserForm?.values?.documentType || purchaser?.documentType;

  // Combine all documents
  const allDocs = [...passengerDocs, ...(purchaserDoc ? [purchaserDoc] : [])];

  return {
    hasIneligibleDoc: allDocs.some((docType) => validDocumentTypeForInsurance(docType) === false),
    passengerDocs: allDocs,
  };
}
